import type { ErrorEvent } from '@sentry/browser';
import { Capacitor } from '@capacitor/core';

/**
 * Crash reporting (Sentry), for uncaught errors in the web build and the native apps alike: the
 * game runs in a WebView there, so the browser SDK sees the same errors. Reports are anonymous:
 * no IP address, user fields, cookies, query strings, locale or headers besides User-Agent (see
 * public/privacy.html, which must stay in step with this file). Players can turn it off in
 * Settings.
 *
 * Builds without `VITE_SENTRY_DSN` (dev, tests, a fork) never load the SDK, so this is a no-op.
 * It is a separate chunk, loaded on demand, to keep it out of the main bundle.
 */
let enabled = false;

const DROPPED_INTEGRATIONS = ['BrowserSession', 'CultureContext'];

/** The longest the game waits for the crash reporter before it starts without it. */
export const LOAD_WAIT_MS = 2000;

/**
 * Starts reporting, if this build has a DSN. `allowed` is the player's saved choice. Await it
 * before booting the game so boot errors are caught; it never rejects, and it never waits more
 * than `LOAD_WAIT_MS`: a download that hangs (a bad connection, a filtering proxy) would otherwise
 * leave the screen blank forever. A late download still starts reporting when it arrives.
 */
export async function initCrashReports(allowed: boolean): Promise<void> {
  enabled = allowed;
  const dsn: string | undefined = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || import.meta.env.DEV) return;
  const started = import('./sentry').then(
    (sentry) => {
      sentry.init({
        dsn,
        release: __APP_RELEASE__,
        environment: Capacitor.getPlatform(), // 'web' | 'ios' | 'android'
        dataCollection: {
          userInfo: false,
          cookies: false,
          httpHeaders: { allow: ['User-Agent'] }, // which browser/OS crashed; nothing else
          urlQueryParams: false,
        },
        // No session pings on every launch (they would be sent even when opted out), and no
        // locale/time zone ('CultureContext'): crashes only, with nothing that hints at where you are.
        integrations: (defaults) => defaults.filter((i) => !DROPPED_INTEGRATIONS.includes(i.name)),
        beforeSend: (event) => (enabled ? scrubEvent(event) : null),
      });
    },
    // A failed chunk load (offline before the first precache) just means no reporting.
    () => undefined,
  );
  await waitAtMost(started, LOAD_WAIT_MS);
}

/**
 * Settles when `work` settles (even by failing) or after `ms`, whichever comes first; never rejects.
 * Exported for tests.
 */
export function waitAtMost(work: Promise<void>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    work.then(done, done);
  });
}

/** Applies the Settings toggle; takes effect for the next error. */
export function setCrashReportsEnabled(on: boolean): void {
  enabled = on;
}

/**
 * Last line of defense before an event leaves the device: drops anything that could identify a
 * player even if an SDK default changes. Exported for tests.
 */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  delete event.user;
  if (event.request) {
    // User-Agent alone stays: it is how Sentry tells which browser/OS version crashed.
    const userAgent = event.request.headers?.['User-Agent'];
    if (userAgent) event.request.headers = { 'User-Agent': userAgent };
    else delete event.request.headers;
    delete event.request.cookies;
    delete event.request.query_string;
    if (event.request.url) event.request.url = event.request.url.split(/[?#]/)[0];
  }
  return event;
}
